import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Alert, Autocomplete, Button, TextField } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import moment from "moment";
import toast from "react-hot-toast";
import { NumericFormat } from "react-number-format";

import { RedeemedInvestment } from "../../actions/redemption";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  width: {
    xs: "calc(100% - 24px)",
    sm: 760,
  },
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "90vh",

  overflowY: "auto",
  overflowX: "hidden",

  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: {
    xs: 2,
    sm: 3,
  },

  outline: "none",
};

const moneyColumn = (id, label, minWidth = 170) => ({
  id,
  label,
  minWidth,
  align: "right",
  format: (value) =>
    Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
});

const columns = [
  { id: "code", label: "Code", minWidth: 120 },
  { id: "investment_type", label: "Investment type", minWidth: 190 },
  moneyColumn("cost", "Original Cost"),
  moneyColumn("total_credited_amount", "Credited Amount"),
  moneyColumn("rediscount_interest", "Rediscount Interest"),
  moneyColumn(
    "interest_charged_before_redemption",
    "Interest Already Charged",
    190,
  ),
  moneyColumn("remaining_interest", "Remaining Interest", 180),
  {
    id: "redemption_date",
    label: "Rediscount Date",
    minWidth: 150,
    align: "right",
  },
  {
    id: "maturity_date",
    label: "Adjusted Maturity Date",
    minWidth: 180,
    align: "right",
  },
  {
    id: "days_charged_before_redemption",
    label: "Days Already Charged",
    minWidth: 170,
    align: "right",
  },
];

const safeText = (value) => String(value ?? "").trim();

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const roundMoney = (value) => {
  const number = safeNumber(value);
  return Number(number.toFixed(2));
};

const fmtMoney = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return "-";

  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (value) => {
  if (!value) return "-";

  const date = moment(value);
  return date.isValid() ? date.format("DD-MMM-YYYY") : "-";
};

const formatTableDate = (value) => {
  if (!value) return "-";

  const date = moment(value);
  return date.isValid() ? date.format("DD-MM-YYYY") : "-";
};

function createData(item) {
  const originalDailyInterest = safeNumber(
    item?.original_daily_interest ?? item?.daily_interest,
  );

  const interestAlreadyCharged = safeNumber(
    item?.interest_charged_before_redemption,
  );

  const calculatedDays =
    originalDailyInterest > 0
      ? Math.round(interestAlreadyCharged / originalDailyInterest)
      : 0;

  return {
    id: item?._id,
    code: item?.code,
    investment_type: item?.investment_type,
    cost: safeNumber(item?.original_cost ?? item?.cost),
    total_credited_amount: safeNumber(item?.total_credited_amount),
    rediscount_interest: safeNumber(
      item?.rediscount_interest ?? item?.interest_after_rediscount,
    ),
    interest_charged_before_redemption: interestAlreadyCharged,
    remaining_interest: safeNumber(
      item?.remaining_interest ?? item?.total_interest,
    ),
    redemption_date: formatTableDate(item?.redemption_date),
    maturity_date: formatTableDate(item?.maturity_date),
    days_charged_before_redemption: safeNumber(
      item?.days_charged_before_redemption ?? calculatedDays,
    ),
  };
}

export default function Redemption() {
  const dispatch = useDispatch();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [open, setOpen] = React.useState(false);

  const redeemed = useSelector((state) => state.redemption);
  const investments = useSelector((state) => state.investments);
  const undefinedInvestments = useSelector(
    (state) => state.approvedUndefinedInvestments,
  );

  const allInvestments = React.useMemo(
    () => [...(investments || []), ...(undefinedInvestments || [])],
    [investments, undefinedInvestments],
  );

  const [data, setData] = React.useState(redeemed || []);

  React.useEffect(() => {
    setData(redeemed || []);
  }, [redeemed]);

  const user = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile") || "{}");
    } catch {
      return {};
    }
  }, []);

  const redeemedBy = React.useMemo(
    () =>
      `${user?.results?.firstName || ""} ${
        user?.results?.lastName || ""
      }`.trim(),
    [user],
  );

  const emptyInvestmentData = React.useCallback(
    () => ({
      code: "",
      redemption_date: "",
      total_credited_amount: "",
      redeemed: "true",
      redeemed_by: redeemedBy,
      interest_charged_before_redemption: "",
      maturity_date: "",
      normal_maturity_date: "",
    }),
    [redeemedBy],
  );

  const [investmentData, setInvestmentData] = React.useState(() =>
    emptyInvestmentData(),
  );

  const resetForm = React.useCallback(() => {
    setInvestmentData(emptyInvestmentData());
  }, [emptyInvestmentData]);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const selectedInvestment = React.useMemo(() => {
    if (!investmentData.code) return null;

    return (
      allInvestments.find(
        (investment) => investment?.code === investmentData.code,
      ) || null
    );
  }, [allInvestments, investmentData.code]);

  const investmentId = selectedInvestment?._id || "";
  const purchaseDate = selectedInvestment?.purchase_date || "";
  const normalMaturityDate = selectedInvestment?.maturity_date || "";
  const originalDailyInterest = safeNumber(selectedInvestment?.daily_interest);
  const originalCost = safeNumber(selectedInvestment?.cost);
  const originalTotalInterest = safeNumber(selectedInvestment?.total_interest);

  // Original principal/actual investment cost.
  // Per the requested rule: cost - total_interest.
  const actualInvestmentCost = React.useMemo(
    () => roundMoney(originalCost - originalTotalInterest),
    [originalCost, originalTotalInterest],
  );

  const hasCreditedAmount =
    investmentData.total_credited_amount !== "" &&
    investmentData.total_credited_amount !== null &&
    investmentData.total_credited_amount !== undefined;

  const totalCreditedAmount = hasCreditedAmount
    ? safeNumber(investmentData.total_credited_amount)
    : 0;

  // Total interest realised from the rediscount.
  // total credited amount - actual investment cost.
  const rediscountInterest = React.useMemo(() => {
    if (!hasCreditedAmount || !selectedInvestment) return "";

    return roundMoney(totalCreditedAmount - actualInvestmentCost);
  }, [
    actualInvestmentCost,
    hasCreditedAmount,
    selectedInvestment,
    totalCreditedAmount,
  ]);

  // Interest was already recognised only up to the last day of the month
  // immediately before the selected rediscount month.
  const previousMonthEnd = React.useMemo(() => {
    if (!investmentData.redemption_date) return "";

    return moment(investmentData.redemption_date)
      .startOf("month")
      .subtract(1, "day")
      .format("YYYY-MM-DD");
  }, [investmentData.redemption_date]);

  const daysChargedBeforeRedemption = React.useMemo(() => {
    if (!purchaseDate || !previousMonthEnd) return 0;

    const start = moment(purchaseDate).startOf("day");
    const end = moment(previousMonthEnd).startOf("day");

    if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
      return 0;
    }

    // Inclusive count: the purchase day and cutoff day are both included.
    return end.diff(start, "days") + 1;
  }, [previousMonthEnd, purchaseDate]);

  const interestChargedBeforeRedemption = React.useMemo(
    () => roundMoney(daysChargedBeforeRedemption * originalDailyInterest),
    [daysChargedBeforeRedemption, originalDailyInterest],
  );

  // Interest still to be recognised after removing what was already charged.
  const remainingInterest = React.useMemo(() => {
    if (rediscountInterest === "") return "";

    return roundMoney(
      safeNumber(rediscountInterest) - interestChargedBeforeRedemption,
    );
  }, [interestChargedBeforeRedemption, rediscountInterest]);

  // "Beginning of the selected date" is treated as the first day of the
  // selected rediscount month.
  const adjustedMaturityDate = React.useMemo(() => {
    if (!investmentData.redemption_date) return "";

    return moment(investmentData.redemption_date)
      .startOf("month")
      .format("YYYY-MM-DD");
  }, [investmentData.redemption_date]);

  const computedPayload = React.useMemo(
    () => ({
      ...investmentData,

      redeemed: "true",
      redemption_type: "rediscount",

      /*
       * Preserve the original investment values.
       * These should not be overwritten during rediscount.
       */
      original_cost: roundMoney(originalCost),
      original_total_interest: roundMoney(originalTotalInterest),
      original_daily_interest: roundMoney(originalDailyInterest),

      normal_maturity_date: normalMaturityDate || null,

      /*
       * Amount actually credited by the institution
       * when the investment was rediscounted.
       */
      total_credited_amount: roundMoney(totalCreditedAmount),

      /*
       * Original principal:
       * cost - original total interest
       */
      actual_investment_cost: roundMoney(actualInvestmentCost),

      /*
       * Total interest realised after rediscount:
       * credited amount - actual investment cost
       */
      rediscount_interest: roundMoney(rediscountInterest),

      /*
       * Interest already recognised before rediscount.
       */
      interest_cutoff_date: previousMonthEnd || null,

      days_charged_before_redemption: Math.max(
        0,
        Number(daysChargedBeforeRedemption) || 0,
      ),

      interest_charged_before_redemption: roundMoney(
        interestChargedBeforeRedemption,
      ),

      /*
       * Rediscount interest less interest already recognised.
       */
      remaining_interest: roundMoney(remainingInterest),

      /*
       * Adjusted maturity date becomes the first day
       * of the selected rediscount month.
       */
      maturity_date: adjustedMaturityDate || null,
    }),
    [
      actualInvestmentCost,
      adjustedMaturityDate,
      daysChargedBeforeRedemption,
      interestChargedBeforeRedemption,
      investmentData,
      normalMaturityDate,
      originalCost,
      originalDailyInterest,
      originalTotalInterest,
      previousMonthEnd,
      rediscountInterest,
      remainingInterest,
      totalCreditedAmount,
    ],
  );

  const options = React.useMemo(
    () =>
      allInvestments
        .map((investment) => investment?.code)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b))),
    [allInvestments],
  );

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const rows = React.useMemo(
    () => (data || []).map((item) => createData(item)),
    [data],
  );

  const redeem = async () => {
    if (!selectedInvestment || !investmentId) {
      toast.error("Please select a valid investment");
      return;
    }

    if (!investmentData.redemption_date) {
      toast.error("Please select the rediscount date");
      return;
    }

    if (!hasCreditedAmount || totalCreditedAmount <= 0) {
      toast.error("Please enter a valid total credited amount");
      return;
    }

    if (
      purchaseDate &&
      moment(investmentData.redemption_date).isBefore(
        moment(purchaseDate),
        "day",
      )
    ) {
      toast.error("Rediscount date cannot be before the purchase date");
      return;
    }

    if (
      normalMaturityDate &&
      moment(investmentData.redemption_date).isAfter(
        moment(normalMaturityDate),
        "day",
      )
    ) {
      toast.error("You cannot rediscount an investment after maturity");
      return;
    }

    try {
      await dispatch(RedeemedInvestment(investmentId, computedPayload));
      setOpen(false);
      resetForm();
    } catch {
      // the action has already shown the error
    }
  };

  return (
    <div>
      <Paper sx={{ width: "100%" }}>
        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={open}
          onClose={handleClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 500 } }}
        >
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Typography
                id="transition-modal-title"
                variant="h5"
                component="h2"
                sx={{ mb: 0.5, fontWeight: 800 }}
              >
                Rediscount Investment
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an investment, enter the rediscount date and the total
                amount credited by the institution.
              </Typography>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  redeem();
                }}
              >
                <Autocomplete
                  // Do not use disablePortal.
                  // The dropdown will render outside the modal container.
                  options={options}
                  value={investmentData.code || null}
                  onChange={(event, newValue) =>
                    setInvestmentData((previous) => ({
                      ...previous,
                      code: newValue || "",
                      redemption_date: "",
                      total_credited_amount: "",
                    }))
                  }
                  sx={{
                    width: "100%",
                    mb: 1.5,
                  }}
                  slotProps={{
                    popper: {
                      sx: {
                        zIndex: (theme) => theme.zIndex.modal + 1,
                      },
                    },
                    paper: {
                      sx: {
                        mt: 0.5,
                        borderRadius: 2,
                        boxShadow: 6,
                      },
                    },
                    listbox: {
                      sx: {
                        maxHeight: 280,
                        overflowY: "auto",
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Investment Code"
                      size="small"
                      required
                    />
                  )}
                />

                {selectedInvestment ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <TextField
                        type="date"
                        size="small"
                        label="Rediscount Date"
                        value={investmentData.redemption_date}
                        onChange={(event) =>
                          setInvestmentData((previous) => ({
                            ...previous,
                            redemption_date: event.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: purchaseDate
                            ? moment(purchaseDate).format("YYYY-MM-DD")
                            : undefined,
                          max: normalMaturityDate
                            ? moment(normalMaturityDate).format("YYYY-MM-DD")
                            : undefined,
                        }}
                        required
                      />

                      <NumericFormat
                        customInput={TextField}
                        label="Total Credited Amount"
                        size="small"
                        thousandSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        value={investmentData.total_credited_amount}
                        onValueChange={(values) =>
                          setInvestmentData((previous) => ({
                            ...previous,
                            total_credited_amount:
                              values.floatValue === undefined
                                ? ""
                                : values.floatValue,
                          }))
                        }
                        required
                      />
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        mb: 2,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Investment Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Confirm the investment values used in the rediscount
                        calculation.
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                          },
                          gap: 1.5,
                        }}
                      >
                        <SummaryItem
                          label="Code"
                          value={safeText(selectedInvestment.code) || "-"}
                        />
                        <SummaryItem
                          label="Investment Type"
                          value={
                            safeText(selectedInvestment.investment_type) || "-"
                          }
                        />
                        <SummaryItem
                          label="Institution"
                          value={
                            safeText(selectedInvestment.institution) || "-"
                          }
                        />
                        <SummaryItem
                          label="Tenure"
                          value={selectedInvestment.tenure ?? "-"}
                        />
                        <SummaryItem
                          label="Original Cost"
                          value={fmtMoney(originalCost)}
                          strong
                        />
                        <SummaryItem
                          label="Original Total Interest"
                          value={fmtMoney(originalTotalInterest)}
                          strong
                        />
                        <SummaryItem
                          label="Actual Investment Cost"
                          value={fmtMoney(actualInvestmentCost)}
                          strong
                        />
                        <SummaryItem
                          label="Original Daily Interest"
                          value={fmtMoney(originalDailyInterest)}
                          strong
                        />
                        <SummaryItem
                          label="Purchase Date"
                          value={fmtDate(purchaseDate)}
                        />
                        <SummaryItem
                          label="Normal Maturity Date"
                          value={fmtDate(normalMaturityDate)}
                        />
                      </Box>
                    </Paper>

                    {investmentData.redemption_date && hasCreditedAmount ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          mb: 2,
                          backgroundColor: "#f7fbff",
                          borderColor: "#c9def2",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800 }}
                        >
                          Rediscount Calculation
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 1.5,
                          }}
                        >
                          <SummaryItem
                            label="Total Credited Amount"
                            value={fmtMoney(totalCreditedAmount)}
                            strong
                          />
                          <SummaryItem
                            label="Actual Investment Cost"
                            value={fmtMoney(actualInvestmentCost)}
                          />
                          <SummaryItem
                            label="Rediscount Interest"
                            value={fmtMoney(rediscountInterest)}
                            strong
                          />
                          <SummaryItem
                            label="Interest Cutoff Date"
                            value={fmtDate(previousMonthEnd)}
                          />
                          <SummaryItem
                            label="Days Already Charged"
                            value={daysChargedBeforeRedemption}
                          />
                          <SummaryItem
                            label="Interest Already Charged"
                            value={fmtMoney(interestChargedBeforeRedemption)}
                          />
                          <SummaryItem
                            label="Remaining Interest"
                            value={fmtMoney(remainingInterest)}
                            strong
                          />
                          <SummaryItem
                            label="Adjusted Maturity Date"
                            value={fmtDate(adjustedMaturityDate)}
                            strong
                          />
                        </Box>

                        {safeNumber(remainingInterest) < 0 ? (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            The remaining interest is negative. This means the
                            interest already recognised is greater than the
                            interest realised from the rediscount, so a reversal
                            adjustment may be required.
                          </Alert>
                        ) : null}
                      </Paper>
                    ) : null}
                  </>
                ) : investmentData.code ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Investment details were not found for the selected code.
                  </Alert>
                ) : null}

                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    color="error"
                    onClick={handleClose}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Submit Rediscount
                  </Button>
                </Box>
              </form>
            </Box>
          </Fade>
        </Modal>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Button
            size="small"
            sx={{ textTransform: "capitalize" }}
            onClick={handleOpen}
            variant="contained"
          >
            Rediscount Investment
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 740 }}>
          <Table
            size="small"
            stickyHeader
            aria-label="rediscounted investments"
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      top: 0,
                      minWidth: column.minWidth,
                      backgroundColor: "#f8f8f8",
                      color: "#000",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover tabIndex={-1} key={row.id || row.code}>
                    {columns.map((column) => {
                      const value = row[column.id];

                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {column.format
                            ? column.format(value)
                            : (value ?? "-")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}

function SummaryItem({ label, value, strong = false }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: strong ? 900 : 700,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
